import { useCustom, useList, useUpdate } from "@refinedev/core";
import {
    Typography,
    Stack,
    IconButton,
    Tooltip,
    Box,
    InputAdornment,
} from "@mui/material";
import { ICoupon } from "@/utils/zod/couponValidation";
import { MaterialReactTable, MRT_ColumnDef, MRT_Row } from "material-react-table";
import { PropsWithChildren, useMemo, useState } from "react";
import { CreateButton, DateField } from "@refinedev/mui";
import { DeleteButton } from "@refinedev/mui";
import { RefineListView } from "@/components";
import { useGo, useNavigation, useTranslate } from "@refinedev/core";
import { useLocation } from "react-router";
import { EditOutlined as EditOutlinedIcon, Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";
import { FormProvider, useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { DatePickerElement } from "react-hook-form-mui/date-pickers";
import { useTableState } from "@/hooks/useTableState";

export const CouponList = ({ children }: PropsWithChildren) => {
    const { mutate: updateCoupon } = useUpdate({
      resource: "cart-coupons"
    });
    const t = useTranslate();
    const { createUrl } = useNavigation();
    const go = useGo();
    const { pathname } = useLocation();
    const [editingCode, setEditingCode] = useState<string | null>(null);
    const methods = useForm<ICoupon>();
    const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<ICoupon>();

    const { data: companyCategory } = useCustom<string[]>({
        url: '/companies/company-categories',
        method: 'get'
    })

    // Function to save changes
    const onSave = methods.handleSubmit((data: any) => {
        updateCoupon({
            id: editingCode as string,
            values: {
                code: editingCode,
                category: data.category,
                description: data.description,
                discount_value: data.discount_value,
                status: data[`status_${editingCode}`],
                start_date: data.start_date,
                expiry_date: data.expiry_date,
                discount_applies_above: data.discount_applies_above,
                discount_type: data.discount_type,
            },
        }, {
            onSuccess: () => {
                setEditingCode(null);
            },
        });
    });

    const columns = useMemo<MRT_ColumnDef<ICoupon>[]>(
        () => [
            {
                accessorKey: "code",
                header: "Coupon Code",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    return row.original.code;
                },
            },
            {
                accessorKey: "description",
                header: "Description",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    if (!isEditing) return row.original.description || '-';
                    
                    return (
                        <TextFieldElement
                            name="description"
                            size="small" 
                            defaultValue={row.original.description}
                            sx={{ width: '100%' }}
                        />
                    );
                },
            },
            {
                accessorKey: "category",
                header: "Company Category",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    if (!isEditing) return row.original.category || '-';
                    
                    return (
                        <RHFAutoCompleteString 
                            name="category"
                            label=""
                            options={companyCategory?.data ?? []}
                            defaultValue={row.original.category}
                            size="small"
                            sx={{ flex: 1 }}
                        />

                    );
                },
            },
            {
                accessorKey: "discount_value",
                header: "Discount Value (%)",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    if (!isEditing) return `${row.original.discount_value}%` || '-';
                    
                    return (
                        <TextFieldElement
                            name="discount_value"
                            size="small" 
                            defaultValue={row.original.discount_value} 
                            type="number"
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                },
                                htmlInput: { 
                                    min: 0, 
                                    max: 100 
                                }
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    height: 40,
                                }
                            }}
                        />
                    );
                },
            },
            {
                accessorKey: "discount_applies_above",
                header: "Discount Applies Above",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    if (!isEditing) return `₹${row.original.discount_applies_above}` || '-';
                    
                    return (
                        <TextFieldElement
                            name="discount_applies_above"
                            size="small" 
                            defaultValue={row.original.discount_applies_above} 
                            type="number"
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                },
                                htmlInput: { 
                                    min: 0, 
                                }
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    height: 40,
                                }
                            }}
                        />
                    );
                },
            },
            {
                accessorKey: "status",
                header: "Status",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    
                    return (
                        <RHFAutoCompleteString
                            instanceId={row.original.code}
                            name={`status_${row.original.code}`}
                            multiple={false}
                            createNew={false}
                            defaultValue={row.original.status}
                            label=""
                            options={["active", "inactive"]}
                            size="small"
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    height: 40,
                                }
                            }}
                            onOptionChange={(value) => {
                                if(isEditing) {
                                    methods.setValue("status", value);
                                } else {
                                    updateCoupon({
                                        resource: "cart-coupons",
                                        id: row.original.code,
                                        values: {  ...row.original, status: value },
                                    });
                                }
                            }}
                        />
                    );
                },
            },
            {
                accessorKey: "start_date",
                header: "Start Date",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    const startDate = isEditing 
                        ? methods.getValues("start_date") || row.original.start_date 
                        : row.original.start_date;
                    if (!isEditing) return <DateField format="DD MMM YYYY" value={startDate} />;
                    
                    return (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePickerElement
                                name="start_date"
                                defaultValue={dayjs(startDate)}
                                format="DD MM YYYY"
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    );
                },
            },
            {
                accessorKey: "expiry_date",
                header: "Expiry Date",
                Cell: ({ row }: { row: MRT_Row<ICoupon> }) => {
                    const isEditing = editingCode === row.original.code;
                    const expiryDate = isEditing 
                        ? methods.getValues("expiry_date") || row.original.expiry_date 
                        : row.original.expiry_date;
                    if (!isEditing) return <DateField format="DD MMM YYYY" value={expiryDate} />;
                    
                    return (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePickerElement
                                name="expiry_date"
                                defaultValue={dayjs(expiryDate)}
                                format="DD MM YYYY"
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    );
                },
            },
            {
                accessorKey: 'actions',
                header: 'Actions',
                size: 180,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: function render({ row }) {
                    const isEditing = editingCode === row.original.code;
                    
                    return (
                        <Stack direction="row" spacing={1}>
                            {isEditing ? (
                                <>
                                    <Tooltip title="Save">
                                        <IconButton 
                                            color="primary" 
                                            onClick={onSave}
                                            size="small"
                                        >
                                            <SaveIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                        <IconButton 
                                            color="error" 
                                            onClick={() => {
                                                methods.setValue("description", row.original.description);
                                                methods.setValue("discount_value", row.original.discount_value);
                                                methods.setValue("start_date", row.original.start_date);
                                                methods.setValue("expiry_date", row.original.expiry_date);
                                                setEditingCode(null);
                                            }}
                                        >
                                        <CloseIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    <Tooltip title="Edit">
                                        <IconButton 
                                            color="primary" 
                                            onClick={() => {
                                                methods.setValue("description", row.original.description);
                                                methods.setValue("discount_value", row.original.discount_value);
                                                methods.setValue("discount_applies_above", row.original.discount_applies_above);
                                                methods.setValue("discount_type", row.original.discount_type);
                                                methods.setValue("start_date", row.original.start_date);
                                                methods.setValue("expiry_date", row.original.expiry_date);
                                                setEditingCode(row.original.code);
                                            }}
                                            size="small"
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <DeleteButton 
                                        hideText 
                                        resource="cart-coupons"
                                        recordItemId={row.original.code}
                                    />
                                </>
                            )}
                        </Stack>
                    );
                },
            },
        ],
        [updateCoupon, editingCode, methods]
    );

    const { data, isLoading, isError } = useList<ICoupon>({
        resource: "cart-coupons",
        pagination: { pageSize: 10 },
    });

    return (
        <>
            <RefineListView
                breadcrumb={false}
                headerButtons={(props) => [
                    <CreateButton
                        {...props.createButtonProps}
                        key="create"
                        size="medium"
                        sx={{ height: "40px" }}
                        onClick={() => {
                            return go({
                                to: `${createUrl("cart-coupons")}`,
                                query: {
                                    to: pathname,
                                },
                                options: {
                                    keepQuery: true,
                                },
                                type: "replace",
                            });
                        }}
                    >
                        {t("coupons.titles.create")}
                    </CreateButton>
                ]}
            >
                <FormProvider {...methods}> 
                <MaterialReactTable
                    columns={columns as any}
                    data={data?.data ?? []}
                    enableTopToolbar
                    enableBottomToolbar
                    enableColumnFilters
                    enableGlobalFilter
                    enablePagination
                    muiTableBodyRowProps={{ hover: true }}
                    muiPaginationProps={{
                        rowsPerPageOptions: [10, 20, 50, 100],
                        showFirstButton: true,
                        showLastButton: true,
                    }}
                    state={{
                        ...tableState,
                        isLoading,
                    }}
                    onColumnFiltersChange={onColumnFiltersChange}
                    onGlobalFilterChange={onGlobalFilterChange}
                    onSortingChange={onSortingChange}
                    muiToolbarAlertBannerProps={
                        isError
                            ? {
                                color: 'error',
                                children: 'Error loading data',
                            }
                            : undefined
                    }
                    enableColumnResizing
                    enableColumnPinning
                    enableFullScreenToggle
                    enableDensityToggle
                    enableGlobalFilterModes
                    positionGlobalFilter="right"
                    getRowId={(row) => row.code ?? ''}
                    initialState={{
                        density: 'compact',
                        pagination: { pageSize: 10, pageIndex: 0 },
                        columnPinning: { left: ['code'], right: ['actions'] },
                    }}
                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            header: 'Actions',
                        },
                    }}
                    renderDetailPanel={({ row }) => (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: '#f5f5f5',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>Coupon Details</Typography>
                            <Stack spacing={1}>
                                <Typography><strong>Code:</strong> {row.original.code}</Typography>
                                <Typography><strong>Description:</strong> {row.original.description}</Typography>
                                <Typography><strong>Discount:</strong> {row.original.discount_value}%</Typography>
                                <Typography><strong>Status:</strong> {row.original.status}</Typography>
                                <Typography><strong>Valid From:</strong> <DateField component="span" format="DD MMM YYYY" value={row.original.start_date} /></Typography>
                                <Typography><strong>Valid Until:</strong> <DateField component="span" format="DD MMM YYYY" value={row.original.expiry_date} /></Typography>
                            </Stack>
                        </Box>
                    )}
                />
                </FormProvider>
            </RefineListView>
            {children}
        </>
    );
};